import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import ConfirmationModal from './ConfirmationModal';
import { adminApi } from '../lib/api';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { extractErrorMessage } from '../lib/utils';
import { useAuth } from '../lib/AuthContext';
import './AdminLeadsManagement.css';

interface Lead {
  id: string;
  name: string;
  email: string;
  company?: string;
  message?: string;
  source: string;
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'LOST';
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface CreateLeadData {
  name: string;
  email: string;
  company: string;
  message: string;
  source: string;
}

interface EditLeadData {
  name: string;
  email: string;
  company: string;
  message: string;
  source: string;
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'LOST';
}

interface LeadStats {
  total: number;
  byStatus: {
    new: number;
    contacted: number;
    qualified: number;
    converted: number;
    lost: number;
  };
  bySource: Array<{
    source: string;
    count: number;
  }>;
  metrics: {
    recentLeads: number;
    conversionRate: number;
    qualificationRate: number;
  };
}

const AdminLeadsManagement: React.FC = () => {
  // Auth context
  const { hasPermission, isSuperAdmin } = useAuth();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [editLeadData, setEditLeadData] = useState<EditLeadData>({
    name: '',
    email: '',
    company: '',
    message: '',
    source: 'website',
    status: 'NEW'
  });
  const [createLeadData, setCreateLeadData] = useState<CreateLeadData>({
    name: '',
    email: '',
    company: '',
    message: '',
    source: 'website'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [stats, setStats] = useState<LeadStats | null>(null);
  
  // Alert hooks
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  const showWarning = useWarningAlert();
  
  // Ref for search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasSearchingRef = useRef(false);
  
  // Enhanced search input handler that preserves focus
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Ensure the input stays focused during typing
    if (searchInputRef.current) {
      // Store cursor position
      const cursorPosition = searchInputRef.current.selectionStart;
      
      // Use setTimeout to restore focus after any potential re-renders
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position
          if (cursorPosition !== null) {
            searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 1);
    }
  };

  const leadsPerPage = 10;

  // Single function to load leads - NO USEEFFECT DEPENDENCIES 
  const loadLeads = async () => {
    try {
      // Show loading only for initial load, use tableLoading for searches
      if (leads.length === 0) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      
      const response = await adminApi.getLeads({
        page: currentPage,
        limit: leadsPerPage,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });

      if (response.success && response.data) {
        setLeads(response.data.leads || []);
        setPagination(response.data.pagination);
      } else {
        const errorMsg = extractErrorMessage(response);
        
        // Handle authentication errors
        if (response.data?.redirect === '/login') {
          window.location.href = '/login';
          return;
        }
        
        // Only show error alert for non-rate-limited errors
        if (!response.message?.includes('Too many')) {
          showError(errorMsg, 'Error Loading Leads');
        }
        
        console.error('Fetch leads error:', errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Failed to connect to server';
      showError(errorMsg, 'Connection Error');
      console.error('Fetch leads error:', err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  // Load stats function
  const loadStats = async () => {
    try {
      const response = await adminApi.getLeadStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Refetch function for CRUD operations
  const refetchLeads = async () => {
    await Promise.all([loadLeads(), loadStats()]);
  };

  // ONLY ONE USEEFFECT - Initial load only
  useEffect(() => {
    Promise.all([loadLeads(), loadStats()]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependencies - only runs once

  // Debounce search input - updates debouncedSearch after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // Short debounce for search input

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Manual trigger when debouncedSearch or statusFilter changes
  useEffect(() => {
    if (currentPage === 1) {
      loadLeads(); // Reload with current filters
    } else {
      setCurrentPage(1); // This will trigger loadLeads via page effect
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  // Manual trigger when page changes
  useEffect(() => {
    if (currentPage > 1) { // Don't run on initial mount
      loadLeads();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Focus management for search input during transitions
  useEffect(() => {
    // Store if user was searching when results become empty
    if (leads.length === 0 && debouncedSearch.length > 0) {
      wasSearchingRef.current = true;
    }
    
    // Restore focus if user was searching and results come back
    if (wasSearchingRef.current && leads.length > 0 && debouncedSearch.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 10);
      wasSearchingRef.current = false;
    }
    
    // Reset flag if search is cleared
    if (debouncedSearch.length === 0) {
      wasSearchingRef.current = false;
    }
  }, [leads.length, debouncedSearch]);

  // Additional focus preservation for input changes
  useEffect(() => {
    // Preserve focus during any re-renders if user is actively typing
    const preserveFocus = () => {
      if (searchQuery.length > 0 && searchInputRef.current && 
          document.activeElement !== searchInputRef.current && 
          !document.activeElement?.closest('.custom-select')) {
        // Only restore focus if user isn't interacting with the select dropdown
        searchInputRef.current.focus();
      }
    };
    
    // Small delay to handle re-renders
    const timer = setTimeout(preserveFocus, 50);
    return () => clearTimeout(timer);
  }, [leads, searchQuery]); // Re-run when leads change

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    // Ensure we're displaying in the user's local timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate difference in UTC to avoid timezone issues
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return formatDate(dateString);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return '#3b82f6';
      case 'CONTACTED': return '#f59e0b';
      case 'QUALIFIED': return '#8b5cf6';
      case 'CONVERTED': return '#10b981';
      case 'LOST': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source.toLowerCase()) {
      case 'website': return '#3b82f6';
      case 'social': return '#8b5cf6';
      case 'email': return '#10b981';
      case 'referral': return '#f59e0b';
      case 'advertising': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setEditLeadData({
      name: lead.name,
      email: lead.email,
      company: lead.company || '',
      message: lead.message || '',
      source: lead.source,
      status: lead.status
    });
    setIsEditModalOpen(true);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setCreateLeadData({
      name: '',
      email: '',
      company: '',
      message: '',
      source: 'website'
    });
    setIsCreateModalOpen(true);
  };

  const handleDeleteLead = async (lead: Lead) => {
    setLeadToDelete(lead);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    try {
      setIsSubmitting(true);
      const response = await adminApi.deleteLead(leadToDelete.id);
      
      if (response.success) {
        await refetchLeads();
        showSuccess(`${leadToDelete.name} has been deleted successfully`, 'Lead Deleted');
      } else {
        showError(response.error || 'Failed to delete lead', 'Delete Failed');
      }
    } catch (err) {
      console.error('Delete lead error:', err);
      showError('Network error occurred while deleting lead', 'Delete Failed');
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
      setLeadToDelete(null);
    }
  };

  const handleSaveLead = async () => {
    if (!selectedLead) return;

    // Validation
    if (!editLeadData.name.trim()) {
      showWarning('Name is required', 'Validation Error');
      return;
    }

    if (!editLeadData.email.trim() || !/\S+@\S+\.\S+/.test(editLeadData.email)) {
      showWarning('Valid email is required', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await adminApi.updateLead(selectedLead.id, editLeadData);
      
      if (response.success) {
        await refetchLeads();
        setIsEditModalOpen(false);
        showSuccess(`${editLeadData.name} has been updated successfully`, 'Lead Updated');
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Update Failed');
      }
    } catch (err) {
      console.error('Update lead error:', err);
      showError('Network error occurred while updating lead', 'Update Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNewLead = async () => {
    // Validation
    if (!createLeadData.name.trim()) {
      showWarning('Name is required', 'Validation Error');
      return;
    }

    if (!createLeadData.email.trim() || !/\S+@\S+\.\S+/.test(createLeadData.email)) {
      showWarning('Valid email is required', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await adminApi.createLead(createLeadData);
      
      if (response.success) {
        await refetchLeads();
        setIsCreateModalOpen(false);
        showSuccess(`${createLeadData.name} has been created successfully`, 'Lead Created');
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Creation Failed');
      }
    } catch (err) {
      console.error('Create lead error:', err);
      showError('Network error occurred while creating lead', 'Creation Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'NEW', label: 'New' },
    { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'LOST', label: 'Lost' }
  ];

  const leadStatusOptions = [
    { value: 'NEW', label: 'New' },
    { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'LOST', label: 'Lost' }
  ];

  const sourceOptions = [
    { value: 'website', label: 'Website' },
    { value: 'social', label: 'Social Media' },
    { value: 'email', label: 'Email Marketing' },
    { value: 'referral', label: 'Referral' },
    { value: 'advertising', label: 'Advertising' },
    { value: 'other', label: 'Other' }
  ];

  // Custom styles for react-select
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.8)',
      border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
      borderRadius: '8px',
      padding: '2px 4px',
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
      cursor: 'pointer',
      '&:hover': {
        border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.95)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 99999999,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      background: state.isSelected 
        ? 'rgba(99, 102, 241, 0.2)' 
        : state.isFocused 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'transparent',
      color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      padding: '10px 14px',
      cursor: 'pointer',
      '&:hover': {
        background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--text-muted)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: 'var(--text-muted)',
      padding: '6px',
      '&:hover': {
        color: 'var(--text-primary)'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  if (loading && leads.length === 0) {
    return (
      <div className="admin-leads-loading">
        <div className="admin-leads-loading-spinner">
          <i className="ri-loader-4-line"></i>
        </div>
        <div className="admin-leads-loading-text">Loading leads...</div>
      </div>
    );
  }

  return (
    <DashboardLayout title="Leads Management">
      <div className="admin-leads">
        {/* Header */}
        <div className="admin-leads-header">
          <div className="admin-leads-header-content">
            <h1 className="admin-leads-title">Lead Management</h1>
            <p className="admin-leads-subtitle">
              Manage customer leads and track conversions across your platform
            </p>
          </div>
          <div className="admin-leads-stats">
            <div className="admin-leads-stat">
              <div className="admin-leads-stat-value">{stats?.total || 0}</div>
              <div className="admin-leads-stat-label">Total Leads</div>
            </div>
            <div className="admin-leads-stat">
              <div className="admin-leads-stat-value">{stats?.byStatus?.converted || 0}</div>
              <div className="admin-leads-stat-label">Converted</div>
            </div>
            <div className="admin-leads-stat">
              <div className="admin-leads-stat-value">{stats?.metrics?.conversionRate || 0}%</div>
              <div className="admin-leads-stat-label">Conv. Rate</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-leads-filters">
          <div className="admin-leads-search">
            <i className="ri-search-line"></i>
            <input
              key="search-input" // Stable key to prevent re-creation
              ref={searchInputRef}
              type="text"
              placeholder="Search leads by name, email, or company..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="admin-leads-search-input"
              autoComplete="off"
            />
          </div>
          <div className="admin-leads-filter-group">
            <Select
              options={statusOptions}
              value={statusOptions.find(option => option.value === statusFilter)}
              onChange={(option) => setStatusFilter(option?.value || 'all')}
              placeholder="Filter by status"
              styles={selectStyles}
              isSearchable={false}
              className="admin-leads-status-filter"
              isDisabled={loading}
            />
            {(hasPermission('leads_create') || isSuperAdmin()) && (
              <button 
                className="admin-leads-create-btn"
                onClick={handleCreateLead}
                disabled={loading || isSubmitting}
              >
                <i className="ri-add-line"></i>
                <span>Create Lead</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading indicator for filters */}
        {loading && leads.length > 0 && (
          <div className="admin-leads-loading-indicator">
            <i className="ri-loader-4-line"></i>
            <span>Updating results...</span>
          </div>
        )}

        {/* Leads Table */}
        <div className="admin-leads-table-container" style={{ position: 'relative' }}>
          {tableLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '0.75rem'
            }}>
              <div style={{ 
                background: 'rgba(23, 23, 28, 0.9)', 
                padding: '1rem 1.5rem', 
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }}></i>
                Searching...
              </div>
            </div>
          )}
          {leads.length === 0 && !loading && !tableLoading ? (
            <div className="admin-leads-empty">
              <i className="ri-user-add-line"></i>
              <h3>No leads found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="admin-leads-table" style={{ opacity: tableLoading ? 0.7 : 1 }}>
              <div className="admin-leads-table-header">
                <div className="admin-leads-table-cell">Lead</div>
                <div className="admin-leads-table-cell">Company</div>
                <div className="admin-leads-table-cell">Status</div>
                <div className="admin-leads-table-cell">Source</div>
                <div className="admin-leads-table-cell">Created</div>
                <div className="admin-leads-table-cell">Actions</div>
              </div>
              <div className="admin-leads-table-body">
                {leads.map((lead) => (
                  <div key={lead.id} className="admin-leads-table-row">
                    <div className="admin-leads-table-cell">
                      <div className="admin-lead-info">
                        <div className="admin-lead-avatar">
                          <i className="ri-user-fill"></i>
                        </div>
                        <div className="admin-lead-details">
                          <div className="admin-lead-name">{lead.name}</div>
                          <div className="admin-lead-email">{lead.email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-leads-table-cell">
                      <span className="admin-lead-company">
                        {lead.company || 'Not specified'}
                      </span>
                    </div>
                    <div className="admin-leads-table-cell">
                      <span 
                        className="admin-lead-status-badge"
                        style={{ backgroundColor: `${getStatusColor(lead.status)}20`, color: getStatusColor(lead.status) }}
                      >
                        {lead.status}
                      </span>
                    </div>
                    <div className="admin-leads-table-cell">
                      <span 
                        className="admin-lead-source-badge"
                        style={{ backgroundColor: `${getSourceColor(lead.source)}20`, color: getSourceColor(lead.source) }}
                      >
                        {lead.source}
                      </span>
                    </div>
                    <div className="admin-leads-table-cell">
                      <span className="admin-lead-created-date">
                        {formatTimeAgo(lead.createdAt)}
                      </span>
                    </div>
                    <div className="admin-leads-table-cell">
                      <div className="admin-lead-actions">
                        {(hasPermission('leads_edit') || isSuperAdmin()) && (
                          <button 
                            className="admin-lead-action-btn"
                            onClick={() => handleEditLead(lead)}
                            title="Edit lead"
                            disabled={isSubmitting}
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        )}
                        {(hasPermission('leads_delete') || isSuperAdmin()) && (
                          <button 
                            className="admin-lead-action-btn admin-lead-delete-btn"
                            onClick={() => handleDeleteLead(lead)}
                            title="Delete lead"
                            disabled={isSubmitting}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="admin-leads-pagination">
            <button 
              className="admin-leads-pagination-btn"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              <i className="ri-arrow-left-line"></i>
              Previous
            </button>
            <div className="admin-leads-pagination-info">
              Page {currentPage} of {pagination.totalPages} ({pagination.totalItems} leads)
            </div>
            <button 
              className="admin-leads-pagination-btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.totalPages || loading}
            >
              Next
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedLead && (
          <div className="admin-leads-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
            <div className="admin-leads-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-leads-modal-header">
                <h3>Edit Lead</h3>
                <button onClick={() => setIsEditModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-leads-modal-content">
                <div className="admin-leads-form">
                  <div className="admin-leads-form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={editLeadData.name}
                      onChange={(e) => setEditLeadData({...editLeadData, name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editLeadData.email}
                      onChange={(e) => setEditLeadData({...editLeadData, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Company</label>
                    <input
                      type="text"
                      value={editLeadData.company}
                      onChange={(e) => setEditLeadData({...editLeadData, company: e.target.value})}
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Message</label>
                    <textarea
                      value={editLeadData.message}
                      onChange={(e) => setEditLeadData({...editLeadData, message: e.target.value})}
                      placeholder="Enter message"
                      rows={3}
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Source</label>
                    <Select
                      options={sourceOptions}
                      value={sourceOptions.find(option => option.value === editLeadData.source)}
                      onChange={(option) => setEditLeadData({...editLeadData, source: option?.value || 'website'})}
                      styles={selectStyles}
                      isSearchable={false}
                      placeholder="Select source"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Status</label>
                    <Select
                      options={leadStatusOptions}
                      value={leadStatusOptions.find(option => option.value === editLeadData.status)}
                      onChange={(option) => setEditLeadData({...editLeadData, status: option?.value as 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'LOST' || 'NEW'})}
                      styles={selectStyles}
                      isSearchable={false}
                      placeholder="Select status"
                    />
                  </div>
                  <div className="admin-leads-form-actions">
                    <button 
                      className="admin-leads-btn admin-leads-btn-secondary"
                      onClick={() => setIsEditModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-leads-btn admin-leads-btn-primary"
                      onClick={handleSaveLead}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <div className="admin-leads-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
            <div className="admin-leads-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-leads-modal-header">
                <h3>Create New Lead</h3>
                <button onClick={() => setIsCreateModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-leads-modal-content">
                <div className="admin-leads-form">
                  <div className="admin-leads-form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={createLeadData.name}
                      onChange={(e) => setCreateLeadData({...createLeadData, name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={createLeadData.email}
                      onChange={(e) => setCreateLeadData({...createLeadData, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Company</label>
                    <input
                      type="text"
                      value={createLeadData.company}
                      onChange={(e) => setCreateLeadData({...createLeadData, company: e.target.value})}
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Message</label>
                    <textarea
                      value={createLeadData.message}
                      onChange={(e) => setCreateLeadData({...createLeadData, message: e.target.value})}
                      placeholder="Enter message"
                      rows={3}
                    />
                  </div>
                  <div className="admin-leads-form-group">
                    <label>Source</label>
                    <Select
                      options={sourceOptions}
                      value={sourceOptions.find(option => option.value === createLeadData.source)}
                      onChange={(option) => setCreateLeadData({...createLeadData, source: option?.value || 'website'})}
                      styles={selectStyles}
                      isSearchable={false}
                      placeholder="Select source"
                    />
                  </div>
                  <div className="admin-leads-form-actions">
                    <button 
                      className="admin-leads-btn admin-leads-btn-secondary"
                      onClick={() => setIsCreateModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-leads-btn admin-leads-btn-primary"
                      onClick={handleCreateNewLead}
                      disabled={isSubmitting || !createLeadData.name || !createLeadData.email}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Lead'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDeleteLead}
          title="Delete Lead"
          message="Are you sure you want to delete this lead? This action cannot be undone and will remove all associated data."
          confirmText="Delete Lead"
          type="danger"
          isLoading={isSubmitting}
        >
          {leadToDelete && (
            <div className="admin-leads-delete-modal-lead">
              <div className="admin-leads-delete-modal-lead-avatar">
                <i className="ri-user-3-line"></i>
              </div>
              <div className="admin-leads-delete-modal-lead-info">
                <h4>{leadToDelete.name}</h4>
                <p>{leadToDelete.email}</p>
                <div className="admin-leads-delete-modal-lead-meta">
                  <span className="admin-leads-delete-modal-lead-company">
                    {leadToDelete.company || 'No company'}
                  </span>
                  <span className="admin-leads-delete-modal-lead-status" 
                        style={{ color: getStatusColor(leadToDelete.status) }}>
                    {leadToDelete.status}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ConfirmationModal>
      </div>
    </DashboardLayout>
  );
};

export default AdminLeadsManagement;
